import { useEffect, useState } from 'react'
import { SparklesIcon } from 'lucide-react'
import { toast } from 'sonner'

import { apiClient } from '@/api/client'
import { useAuth } from '@/contexts/AuthContext'
import { formatApiError } from '@/utils/httpErrors'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'

export interface NegotiationLever {
  lever: string
  description: string
  finding_ids: number[]
}

export interface BuyerDealBriefData {
  deal_id: string
  summary: string
  negotiation_levers: NegotiationLever[]
  model_version: string
  generated_at: string
  approved_by: string | null
  approved_at: string | null
  released_at: string | null
}

interface Props {
  dealId: string
}

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

export function BuyerDealBriefPanel({ dealId }: Props) {
  const { user } = useAuth()
  const canManage = user?.role === 'cpa' || user?.role === 'super_admin'

  const [data, setData] = useState<BuyerDealBriefData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [generating, setGenerating] = useState(false)
  const [approving, setApproving] = useState(false)
  const [editSummary, setEditSummary] = useState('')

  useEffect(() => {
    let cancelled = false

    async function load() {
      setLoading(true)
      setError(null)
      try {
        const res = await apiClient.get<BuyerDealBriefData>(
          `/qoe/deals/${dealId}/buyer-brief`
        )
        if (cancelled) return
        setData(res.data)
        setEditSummary(res.data.summary)
      } catch (err: unknown) {
        if (cancelled) return
        const status = (err as { response?: { status: number } })?.response?.status
        if (status === 404) {
          setData(null)
        } else {
          setError('Failed to load buyer brief')
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    load()
    return () => {
      cancelled = true
    }
  }, [dealId])

  async function handleGenerate() {
    setGenerating(true)
    try {
      const res = await apiClient.post<BuyerDealBriefData>(
        `/qoe/deals/${dealId}/generate-buyer-brief`
      )
      setData(res.data)
      setEditSummary(res.data.summary)
      toast.success('Draft generated — review before releasing')
    } catch (err: unknown) {
      toast.error(formatApiError(err))
    } finally {
      setGenerating(false)
    }
  }

  async function handleApprove() {
    setApproving(true)
    try {
      const res = await apiClient.post<BuyerDealBriefData>(
        `/qoe/deals/${dealId}/buyer-brief/approve`,
        { summary: editSummary }
      )
      setData(res.data)
      setEditSummary(res.data.summary)
      toast.success('Approved and released to buyer portal')
    } catch (err: unknown) {
      toast.error(formatApiError(err))
    } finally {
      setApproving(false)
    }
  }

  if (loading) {
    return (
      <div className="rounded-lg border bg-card p-4 flex flex-col gap-3">
        <Skeleton className="h-5 w-56" />
        <Skeleton className="h-24 w-full" />
      </div>
    )
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    )
  }

  // NOT_GENERATED
  if (!data) {
    return (
      <div className="rounded-lg border bg-card p-4 flex flex-col gap-3">
        <div className="flex items-center gap-2">
          <SparklesIcon className="h-4 w-4 text-brand-primary" />
          <span className="font-medium text-sm text-brand-text">Buyer Negotiation Brief</span>
        </div>
        <p className="text-sm text-muted-foreground">
          No buyer brief has been generated for this deal yet.
        </p>
        {canManage && (
          <Button size="sm" onClick={handleGenerate} disabled={generating} className="w-fit">
            {generating ? 'Generating…' : 'Generate AI Draft'}
          </Button>
        )}
      </div>
    )
  }

  const isReleased = !!data.released_at
  const isApproved = !!data.approved_at
  const levers = data.negotiation_levers ?? []

  return (
    <div className="rounded-lg border bg-card p-4 flex flex-col gap-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <SparklesIcon className="h-4 w-4 text-brand-primary" />
          <span className="font-medium text-sm text-brand-text">Buyer Negotiation Brief</span>
        </div>
        {isReleased ? (
          <Badge variant="default" className="bg-green-600 hover:bg-green-600">
            Released to Buyer · {fmtDate(data.released_at!)}
          </Badge>
        ) : isApproved ? (
          <Badge variant="default" className="bg-green-600 hover:bg-green-600">
            Approved by {data.approved_by} · {fmtDate(data.approved_at!)}
          </Badge>
        ) : (
          <Badge variant="outline" className="border-yellow-500 text-yellow-700">
            Pending CPA Approval
          </Badge>
        )}
      </div>

      {canManage && !isApproved ? (
        <Textarea
          className="min-h-32 text-sm"
          value={editSummary}
          onChange={e => setEditSummary(e.target.value)}
        />
      ) : (
        <p className="text-sm whitespace-pre-wrap text-muted-foreground">{data.summary}</p>
      )}

      <div className="space-y-2">
        <p className="text-xs font-medium text-brand-text uppercase tracking-wide">
          Negotiation Levers
        </p>
        {levers.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No material negotiation levers triggered by scored findings.
          </p>
        ) : (
          <ul className="space-y-2">
            {levers.map(lever => (
              <li key={lever.lever} className="text-sm">
                <span className="font-medium text-brand-text">{lever.lever}:</span>{' '}
                <span className="text-muted-foreground">{lever.description}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <p className="text-xs text-muted-foreground">
        Drafted by {data.model_version} on {fmtDate(data.generated_at)}. This is an AI-drafted
        brief of scored findings — not investment advice.
      </p>

      {canManage && !isApproved && (
        <div className="flex gap-2">
          <Button size="sm" onClick={handleApprove} disabled={approving}>
            {approving ? 'Approving…' : 'Approve & Release to Buyer'}
          </Button>
          <Button size="sm" variant="outline" onClick={handleGenerate} disabled={generating}>
            {generating ? 'Regenerating…' : 'Regenerate Draft'}
          </Button>
        </div>
      )}
    </div>
  )
}

export default BuyerDealBriefPanel
